import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { getPlan } from '@/models/plans/ports'

export type AccountType = 'essentials' | 'growth' | 'business' | 'special'

export type BillingType = 'none' | 'monthly' | 'annual'

type State = 'ACTIVE' | 'PENDING'

type Product =
  | 'essentials-plan-monthly'
  | 'growth-plan-monthly'
  | 'growth-plan-monthly-coupon-wbrothers'
  | 'growth-plan-annual'
  | 'business-plan-monthly'
  | 'business-plan-annual'
  | 'business-plan-special'

type Subscription = {
  subscriptionId: string
  state: string
  plan: string
  startDate: string
  chargedThroughDate: string
}

type Account = {
  account_type: AccountType
  billing_type: BillingType
}

export type Plan = Account & {
  plan: Product
  start_date: string
  charged_through_date: string
}

type ReturnPlan = Plan | undefined

type PlanTuple = {
  active?: Plan
  next?: Plan
}

type Error = {
  message: string
}

export const parsePlan = (product?: Product): [AccountType, BillingType] => {
  if (!product) return ['essentials', 'none']
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [plan, _, billing] = product.split('-')
  if (product === 'business-plan-special') return ['special', 'none']
  if (!isAccountType(plan) || !isBillingType(billing)) return ['essentials', 'none']
  return [plan, billing]
}

const isAccountType = (account?: string): account is AccountType =>
  !!account && ['essentials', 'growth', 'business', 'special'].indexOf(account) >= 0

const isBillingType = (billing?: string): billing is BillingType =>
  !!billing && ['none', 'monthly', 'annual'].indexOf(billing) >= 0

const isPlan = (product?: string): product is Product =>
  !!product &&
  [
    'essentials-plan-monthly',
    'growth-plan-monthly',
    'growth-plan-monthly-coupon-wbrothers',
    'growth-plan-annual',
    'business-plan-monthly',
    'business-plan-annual',
    'business-plan-special'
  ].indexOf(product) >= 0

const getAccount = (product: Product): Account => {
  const [plan, billing] = parsePlan(product)
  return {
    account_type: plan,
    billing_type: plan !== 'essentials' ? billing : 'none'
  }
}

const getData = async (): Promise<Subscription[]> => {
  try {
    const result = await getPlan()
    if (!result) throw new Error('Empty data')
    const cards: Subscription[] = result.body
    return cards
  } catch (err) {
    throw err
  }
}

const formatPlan = (subscription?: Subscription): ReturnPlan => {
  if (!subscription) return
  const plan = subscription.plan
  if (!isPlan(plan)) return
  return {
    ...getAccount(plan),
    plan,
    start_date: subscription.startDate,
    charged_through_date: subscription.chargedThroughDate
  }
}

const empty = { active: undefined, next: undefined }

const getFirstWithState = (data: Subscription[], target: State): Subscription | undefined => {
  const index = data.findIndex(({ state }) => state === target)
  if (index < 0) return
  return data[index]
}

const select = (data: Subscription[]): PlanTuple => {
  if (!data) return empty
  const subscription = getFirstWithState(data, 'ACTIVE')
  const nextCycle = getFirstWithState(data, 'PENDING')
  const active = formatPlan(subscription)
  const next = formatPlan(nextCycle)
  return { active, next }
}

type Return = Omit<UseQueryResult<PlanTuple, Error>, 'data'> & { data: PlanTuple }

const usePlan = (): Return => {
  const { data, ...rest } = useQuery({
    queryKey: ['user', 'plans', 'current'],
    queryFn: getData,
    staleTime: 30000,
    select
  })
  return { data: data ? data : empty, ...rest }
}

export default usePlan
